import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "@/components/icons";

/**
 * Página legal reutilizable — sirve /privacidad, /terminos y /legal.
 * Look Apple minimalista, coherente con Login/Register.
 */

const CONTENT = {
  privacidad: {
    title: "Política de Privacidad",
    subtitle: "Cómo tratamos tu información en ConsuDoctor",
    updated: "Actualizada el 24 de febrero de 2026",
    sections: [
      {
        h: "Qué recopilamos",
        p: "Recopilamos únicamente los datos necesarios para prestar el servicio: correo electrónico, nombre, especialidad médica, información de tus pacientes y consultas. Toda la información clínica se guarda cifrada.",
      },
      {
        h: "Cómo usamos los datos",
        p: "Los datos se usan para permitirte iniciar sesión, gestionar tus pacientes, emitir recetas y auditar eventos clínicos. Nunca vendemos ni compartimos tu información con terceros con fines comerciales.",
      },
      {
        h: "Cumplimiento legal",
        p: "ConsuDoctor cumple con la Ley 42-01 de Salud de la República Dominicana, y aplica los estándares HIPAA en el tratamiento de datos clínicos.",
      },
      {
        h: "Tus derechos",
        p: "Puedes solicitar acceso, rectificación o eliminación de tus datos en cualquier momento escribiendo a soporte@consudoctor.com.",
      },
    ],
  },
  terminos: {
    title: "Términos de Uso",
    subtitle: "Reglas de uso del servicio ConsuDoctor",
    updated: "Actualizados el 24 de febrero de 2026",
    sections: [
      {
        h: "Aceptación",
        p: "Al crear una cuenta aceptas cumplir con estos Términos. Si no estás de acuerdo, no utilices el servicio.",
      },
      {
        h: "Uso profesional",
        p: "ConsuDoctor es una herramienta destinada a profesionales de la salud debidamente acreditados. La emisión de recetas y diagnósticos es responsabilidad exclusiva del profesional que las firma.",
      },
      {
        h: "Cuenta y seguridad",
        p: "Eres responsable de mantener segura tu contraseña. Notifícanos cualquier uso no autorizado para bloquear el acceso.",
      },
      {
        h: "Suspensión",
        p: "Nos reservamos el derecho de suspender cuentas que hagan uso indebido de la plataforma o infrinjan la ley aplicable.",
      },
    ],
  },
  legal: {
    title: "Aviso Legal",
    subtitle: "Información legal de ConsuDoctor",
    updated: "Actualizado el 24 de febrero de 2026",
    sections: [
      {
        h: "Titularidad",
        p: "ConsuDoctor es una plataforma operada por ConsuDoctor SRL, con domicilio en la República Dominicana.",
      },
      {
        h: "Propiedad intelectual",
        p: "Todo el contenido, marca, logos y código de la plataforma pertenecen a ConsuDoctor SRL. Está prohibida su reproducción sin autorización expresa.",
      },
      {
        h: "Limitación de responsabilidad",
        p: "ConsuDoctor no sustituye el juicio clínico del profesional. La empresa no se responsabiliza por decisiones clínicas tomadas con base en la información almacenada.",
      },
      {
        h: "Contacto",
        p: "Para requerimientos legales escribe a legal@consudoctor.com.",
      },
    ],
  },
};

export default function LegalPage() {
  const location = useLocation();
  const slug = location.pathname.replace("/", "") || "privacidad";
  const data = CONTENT[slug] || CONTENT.privacidad;

  return (
    <div className="min-h-screen w-full bg-white flex flex-col relative">
      {/* Logo top-left absoluto — coherente con Login/Register */}
      <div className="absolute top-4 left-6 sm:top-6 sm:left-8">
        <Link to="/">
          <img
            src="/brand/consudoctor-icon.png"
            alt="ConsuDoctor"
            className="w-8 h-8 sm:w-10 sm:h-10 object-contain opacity-70 hover:opacity-100 transition"
          />
        </Link>
      </div>

      {/* Cuerpo centrado */}
      <div className="flex-1 flex items-start justify-center px-4 pt-24 pb-16">
        <div className="w-full max-w-[720px]" data-testid={`legal-page-${slug}`}>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-[13px] text-black/55 hover:text-black transition mb-6"
            data-testid="legal-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
            Volver
          </Link>

          <h1
            data-testid="legal-title"
            className="text-[32px] sm:text-[38px] font-semibold text-black tracking-tight leading-tight"
          >
            {data.title}
          </h1>
          <p className="text-[15px] text-black/55 mt-2">{data.subtitle}</p>
          <p className="text-[12px] text-black/40 mt-1">{data.updated}</p>

          <div className="mt-10 space-y-8">
            {data.sections.map((s) => (
              <section key={s.h} className="border-t border-black/[0.06] pt-6">
                <h2 className="text-[18px] font-semibold text-black mb-2">
                  {s.h}
                </h2>
                <p className="text-[14px] text-black/65 leading-relaxed">
                  {s.p}
                </p>
              </section>
            ))}
          </div>
        </div>
      </div>

      {/* Footer minimalista */}
      <footer className="w-full bg-[#F7F8FA] border-t border-black/[0.05]">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-[12px] text-black/50">
          <span>Copyright © {new Date().getFullYear()} ConsuDoctor</span>
          <Link to="/terminos" className="hover:text-black transition">
            Términos de uso
          </Link>
          <Link to="/privacidad" className="hover:text-black transition">
            Política de privacidad
          </Link>
          <Link to="/legal" className="hover:text-black transition">
            Legal
          </Link>
        </div>
      </footer>
    </div>
  );
}
